import React, { useCallback, useState, useEffect } from 'react';
import axios from 'axios';
import {
  AppRemixIcon,
  AppDatePicker,
  AppInput,
  AppMultiSelect,
  AppSingleSelect,
} from '@/layout/AppComponents';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import Moment from 'moment';
import { UnmountClosed } from 'react-collapse/lib/UnmountClosed';
import styled from 'styled-components';
import ReactDOMServer from 'react-dom/server';
import * as currentPageAction from '../../../../../store/actions/currentPageAction';
import Translator from '../../../../components/helpers/Translator';
import colors from '../../../../../constants/colors';
import { axiosPath, path } from '../../../../../routing/helpers/router';
import { getCAFilterInitialValues } from '../../../../../minions/forms';
import AuxSidebar from '../../../../Sidebar/AuxSidebar';
import { useIsFirstRender } from '../../../../../hooks/isFirstRender';
import useIsMounted from '../../../../components/hooks/useIsMounted';

const FilterSidebarBlock = () => {
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const isFirstRender = useIsFirstRender();
  const { moduleBridgeHex, view, filtersHex } = useParams();
  const auxSidebar = useSelector(
    (state) => state.currentPage?.auxSidebar?.opened
  );
  const filters = useSelector((state) => state?.currentPage?.filters);
  const moduleData = useSelector((state) => state?.module?.[moduleBridgeHex]?.moduleData);
  const userBridge = useSelector(
    (state) => state?.module?.[moduleBridgeHex]?.userBridge
  );
  const [openOnce, setOpenOnce] = useState(true);
  const [dateFilters, setDateFilters] = useState(false);
  const [fieldFilters, setFieldFilters] = useState(false);
  const [textFilters, setTextFilters] = useState(false);
  const [plants, setPlants] = useState([]);
  const [noYesOptions] = useState([
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-legalCompliance" name="no" />
      ),
      value: 0,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-legalCompliance" name="yes" />
      ),
      value: 1,
    },
  ]);

  useEffect(() => {
    if (view === 'my-plants' && userBridge?.permissions?.plants?.length > 0) {
      setPlants(
        moduleData?.plants?.filter((p) =>
          userBridge?.permissions?.plants.includes(p.value)
        )
      );
    } else {
      setPlants(moduleData?.plants);
    }
  }, [view, userBridge]);

  const handleAuxSidebar = () => {
    if (!openOnce) setOpenOnce(true);
    if (auxSidebar !== 'filters') {
      dispatch(currentPageAction.change('auxSidebar', { opened: 'filters' }));
    } else dispatch(currentPageAction.change('auxSidebar', { opened: null }));
  };

  const goToEvaluation = useCallback(
    (hexCode) =>
      navigate(
        path({
          name: 'user_module_correctiveActions_task_list',
          params: {
            moduleBridgeHex,
            view,
            page: 1,
            filtersHex: hexCode,
          },
        })
      ),
    [moduleBridgeHex, view]
  );

  // > GENERATE FILTER HEX CODE
  const generateFilterHexCode = useCallback(
    async (payload, { setSubmitting }) =>
      axios
        .post(
          axiosPath(
            'api_r_user_module_correctiveActions_task_list_generateFilterHexCode'
          ),
          { moduleBridgeHex, filters: payload }
        )
        .then((r) => {
          setSubmitting(false);
          goToEvaluation(r.data?.hexCode);
          dispatch(currentPageAction.change('auxSidebar', { opened: null }));
        }),
    [moduleBridgeHex, view]
  );

  const handleOnSubmit = (payload, { setSubmitting }) => {
    generateFilterHexCode(payload, { setSubmitting });
  };
  // < GENERATE FILTER HEX CODE

  // > FILTERS
  const getFilters = useCallback(
    async () =>
      axios
        .post(
          axiosPath('api_r_user_module_correctiveActions_task_list_getFilters'),
          { moduleBridgeHex, filtersHex }
        )
        .then((r) => {
          if (isMounted.current) {
            dispatch(currentPageAction.change('filters', r.data));
          }
        }),
    [moduleBridgeHex, filtersHex]
  );

  const removeFilters = (resetForm) => {
    resetForm();
    goToEvaluation(0);
  };

  useEffect(() => {
    if (!isFirstRender && view) dispatch(currentPageAction.empty());
    if (filtersHex !== '0') {
      getFilters();
    } else {
      dispatch(
        currentPageAction.change('filters', getCAFilterInitialValues(), true)
      );
    }
  }, [filtersHex, view]);
  // < FILTERS

  return (
    <>
      <li
        className={auxSidebar && auxSidebar === 'filters' ? 'active' : ''}
        onClick={() => handleAuxSidebar()}
      >
        <span className="sidebar-element">
          <AppRemixIcon icon="equalizer" className="sidebar-icon" />{' '}
          <span className="sb-title">
            <Translator file="module-legalCompliance" name="filtering" />
          </span>
        </span>
      </li>
      <AuxSidebar
        title={<Translator file="module-legalCompliance" name="filtering" />}
        isOpen={auxSidebar && auxSidebar === 'filters'}
        width="350px"
        position="right"
        onClose={() => handleAuxSidebar()}
      >
        {openOnce && (
          <>
            {filters && (
              <Formik
                validateOnChange={false}
                validateOnBlur={false}
                enableReinitialize
                initialValues={filters}
                onSubmit={handleOnSubmit}
              >
                {({
                  setFieldValue,
                  handleSubmit,
                  values,
                  isSubmitting,
                  resetForm,
                }) => (
                  <>
                    <div className="d-flex mt-3 mb-3">
                      <span
                        className="btn btn-info btn-block"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                      >
                        <Translator
                          file="module-legalCompliance"
                          name="filtering"
                        />
                      </span>
                      {filtersHex !== '0' && (
                        <span
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="delete.filters"
                            />
                          )}
                          className="btn btn-outline-danger ml-1"
                          onClick={() => removeFilters(resetForm)}
                        >
                          <AppRemixIcon icon="delete-bin-6" />
                        </span>
                      )}
                    </div>
                    <FilterBlockTitle
                      onClick={() => setDateFilters(!dateFilters)}
                    >
                      <AppRemixIcon
                        icon={dateFilters ? 'arrow-down-s' : 'arrow-right-s'}
                        filled
                      />
                      <Translator
                        file="module-legalCompliance"
                        name="filters.by.date"
                      />
                    </FilterBlockTitle>
                    <UnmountClosed isOpened={dateFilters}>
                      <div className="row">
                        <div className="col-lg-12">
                          <h6>
                            <small>
                              <Translator
                                file="module-correctiveActions"
                                name="issue"
                              />
                            </small>
                          </h6>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-lg-6">
                          <AppDatePicker
                            title={ReactDOMServer.renderToString(
                              <Translator
                                file="module-lawFinder"
                                name="module.lf.filter.from"
                              />
                            )}
                            value={
                              values.dateStampFrom
                                ? Moment.unix(values.dateStampFrom).format(
                                    'DD/MM/YYYY'
                                  )
                                : ''
                            }
                            onChange={(date) =>
                              setFieldValue(
                                'dateStampFrom',
                                date ? Moment(date, 'DD/MM/YYYY').unix() : ''
                              )
                            }
                            placeholder="dd/mm/yyyy"
                            isClearable
                            showMonthDropdown
                            showYearDropdown
                          />
                        </div>
                        <div className="col-lg-6">
                          <AppDatePicker
                            title={ReactDOMServer.renderToString(
                              <Translator
                                file="module-lawFinder"
                                name="module.lf.filter.to"
                              />
                            )}
                            value={
                              values.dateStampTo
                                ? Moment.unix(values.dateStampTo).format(
                                    'DD/MM/YYYY'
                                  )
                                : ''
                            }
                            onChange={(date) =>
                              setFieldValue(
                                'dateStampTo',
                                date ? Moment(date, 'DD/MM/YYYY').unix() : ''
                              )
                            }
                            placeholder="dd/mm/yyyy"
                            isClearable
                            showMonthDropdown
                            showYearDropdown
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-lg-12">
                          <hr />
                          <h6>
                            <small>
                              <Translator
                                file="module-correctiveActions"
                                name="module.ca.limitStamp"
                              />
                            </small>
                          </h6>
                        </div>
                        <div className="col-lg-6">
                          <span
                            className="btn btn-sm btn-outline-light btn-block"
                            onClick={() => {
                              setFieldValue('limitStampFrom', '');
                              setFieldValue(
                                'limitStampTo',
                                Moment().subtract(1, 'months').unix()
                              );
                            }}
                          >
                            <Translator
                              file="module-correctiveActions"
                              name="less.than.a.month"
                            />
                          </span>
                          <span
                            className="btn btn-sm btn-outline-light btn-block"
                            onClick={() => {
                              setFieldValue('limitStampFrom', Moment().unix());
                              setFieldValue(
                                'limitStampTo',
                                Moment().add(6, 'months').unix()
                              );
                            }}
                          >
                            <Translator file="filter" name="filter.date.3" />
                          </span>
                        </div>
                        <div className="col-lg-6">
                          <span
                            className="btn btn-sm btn-outline-light btn-block"
                            onClick={() => {
                              setFieldValue('limitStampFrom', Moment().unix());
                              setFieldValue(
                                'limitStampTo',
                                Moment().add(3, 'months').unix()
                              );
                            }}
                          >
                            <Translator file="filter" name="filter.date.2" />
                          </span>
                          <span
                            className="btn btn-sm btn-outline-light btn-block"
                            onClick={() => {
                              setFieldValue('limitStampFrom', Moment().unix());
                              setFieldValue(
                                'limitStampTo',
                                Moment().add(1, 'years').unix()
                              );
                            }}
                          >
                            <Translator file="filter" name="filter.date.4" />
                          </span>
                        </div>
                        <div className="col-lg-6">
                          <AppDatePicker
                            title={ReactDOMServer.renderToString(
                              <Translator
                                file="module-lawFinder"
                                name="module.lf.filter.from"
                              />
                            )}
                            value={
                              values.limitStampFrom
                                ? Moment.unix(values.limitStampFrom).format(
                                    'DD/MM/YYYY'
                                  )
                                : ''
                            }
                            onChange={(date) =>
                              setFieldValue(
                                'limitStampFrom',
                                date ? Moment(date, 'DD/MM/YYYY').unix() : ''
                              )
                            }
                            placeholder="dd/mm/yyyy"
                            isClearable
                            showMonthDropdown
                            showYearDropdown
                          />
                        </div>
                        <div className="col-lg-6">
                          <AppDatePicker
                            title={ReactDOMServer.renderToString(
                              <Translator
                                file="module-lawFinder"
                                name="module.lf.filter.to"
                              />
                            )}
                            value={
                              values.limitStampTo
                                ? Moment.unix(values.limitStampTo).format(
                                    'DD/MM/YYYY'
                                  )
                                : ''
                            }
                            onChange={(date) =>
                              setFieldValue(
                                'limitStampTo',
                                date ? Moment(date, 'DD/MM/YYYY').unix() : ''
                              )
                            }
                            placeholder="dd/mm/yyyy"
                            isClearable
                            showMonthDropdown
                            showYearDropdown
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-lg-12">
                          <h6>
                            <small>
                              <Translator
                                file="module-correctiveActions"
                                name="module.ca.export-closed"
                              />
                            </small>
                          </h6>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-lg-6">
                          <AppDatePicker
                            title={ReactDOMServer.renderToString(
                              <Translator
                                file="module-lawFinder"
                                name="module.lf.filter.from"
                              />
                            )}
                            value={
                              values.closedFrom
                                ? Moment.unix(values.closedFrom).format(
                                    'DD/MM/YYYY'
                                  )
                                : ''
                            }
                            onChange={(date) =>
                              setFieldValue(
                                'closedFrom',
                                date ? Moment(date, 'DD/MM/YYYY').unix() : ''
                              )
                            }
                            placeholder="dd/mm/yyyy"
                            isClearable
                            showMonthDropdown
                            showYearDropdown
                          />
                        </div>
                        <div className="col-lg-6">
                          <AppDatePicker
                            title={ReactDOMServer.renderToString(
                              <Translator
                                file="module-lawFinder"
                                name="module.lf.filter.to"
                              />
                            )}
                            value={
                              values.closedTo
                                ? Moment.unix(values.closedTo).format(
                                    'DD/MM/YYYY'
                                  )
                                : ''
                            }
                            onChange={(date) =>
                              setFieldValue(
                                'dateStampTo',
                                date ? Moment(date, 'DD/MM/YYYY').unix() : ''
                              )
                            }
                            placeholder="dd/mm/yyyy"
                            isClearable
                            showMonthDropdown
                            showYearDropdown
                          />
                        </div>
                      </div>
                    </UnmountClosed>
                    <FilterBlockTitle
                      onClick={() => setFieldFilters(!fieldFilters)}
                    >
                      <AppRemixIcon
                        icon={fieldFilters ? 'arrow-down-s' : 'arrow-right-s'}
                        filled
                      />
                      <Translator
                        file="module-legalCompliance"
                        name="filters.by.features"
                      />
                    </FilterBlockTitle>
                    <UnmountClosed isOpened={fieldFilters}>
                      <>
                        <AppMultiSelect
                          options={moduleData?.responsiblesAndIssuers}
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-correctiveActions"
                              name="responsible"
                            />
                          )}
                          value={values.responsibles}
                          onChange={(opt) => setFieldValue('responsibles', opt)}
                          hasSearchBox
                          isClearable
                        />
                        <AppMultiSelect
                          options={moduleData?.systems}
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-correctiveActions"
                              name="module.ca.systems"
                            />
                          )}
                          value={values.systems}
                          onChange={(opt) => setFieldValue('systems', opt)}
                          hasSearchBox
                          isClearable
                        />
                        <AppMultiSelect
                          options={plants}
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-correctiveActions"
                              name="centre"
                            />
                          )}
                          value={values.plants}
                          onChange={(opt) => setFieldValue('plants', opt)}
                          hasSearchBox
                          isClearable
                        />
                        <AppMultiSelect
                          options={moduleData?.sources}
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-correctiveActions"
                              name="module.ca.source"
                            />
                          )}
                          value={values.sources}
                          onChange={(opt) => setFieldValue('sources', opt)}
                          hasSearchBox
                          isClearable
                        />
                        <AppMultiSelect
                          options={moduleData?.types}
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-correctiveActions"
                              name="module.ca.task-type"
                            />
                          )}
                          value={values.types}
                          onChange={(opt) => setFieldValue('types', opt)}
                          hasSearchBox
                          isClearable
                        />
                        <AppMultiSelect
                          options={moduleData?.responsiblesAndIssuers}
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-correctiveActions"
                              name="module.ca.maker"
                            />
                          )}
                          value={values.issuers}
                          onChange={(opt) => setFieldValue('issuers', opt)}
                          hasSearchBox
                          isClearable
                        />
                        <AppInput
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-correctiveActions"
                              name="module.ca.code"
                            />
                          )}
                          placeholder={
                            <Translator
                              file="module-legalCompliance"
                              name="split.by.commas"
                            />
                          }
                          icon={<AppRemixIcon icon="fingerprint" />}
                          value={values.code}
                          onChange={(opt) => setFieldValue('code', opt)}
                          isClearable
                        />
                        <AppSingleSelect
                          options={moduleData?.tasksInDependencyGroup}
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-correctiveActions"
                              name="filter.by.dependencies"
                            />
                          )}
                          value={values?.showTaskDependencyGroup}
                          onChange={(opt) =>
                            setFieldValue('showTaskDependencyGroup', opt)
                          }
                          hasSearchBox
                        />
                        <AppSingleSelect
                          options={noYesOptions}
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-correctiveActions"
                              name="module.ca.filters.options.show-closed"
                            />
                          )}
                          value={values?.isClosed}
                          onChange={(opt) => setFieldValue('isClosed', opt)}
                        />
                        <AppSingleSelect
                          options={noYesOptions}
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-correctiveActions"
                              name="module.ca.filters.options.show-open"
                            />
                          )}
                          value={values?.isOpened}
                          onChange={(opt) => setFieldValue('isOpened', opt)}
                        />
                        <AppSingleSelect
                          options={noYesOptions}
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-correctiveActions"
                              name="module.ca.close-request"
                            />
                          )}
                          value={values?.hasCloseRequest}
                          onChange={(opt) =>
                            setFieldValue('hasCloseRequest', opt)
                          }
                        />
                      </>
                    </UnmountClosed>
                    <FilterBlockTitle
                      onClick={() => setTextFilters(!textFilters)}
                    >
                      <AppRemixIcon
                        icon={textFilters ? 'arrow-down-s' : 'arrow-right-s'}
                        filled
                      />
                      <Translator
                        file="module-legalCompliance"
                        name="filters.by.text"
                      />
                    </FilterBlockTitle>
                    <UnmountClosed isOpened={textFilters}>
                      <>
                        <AppInput
                          title={
                            <Translator
                              file="module-legalCompliance"
                              name="text"
                            />
                          }
                          icon={<AppRemixIcon icon="file-text" />}
                          value={values.description}
                          onChange={(opt) => setFieldValue('description', opt)}
                          isClearable
                        />
                      </>
                    </UnmountClosed>
                  </>
                )}
              </Formik>
            )}
          </>
        )}
      </AuxSidebar>
    </>
  );
};
export default FilterSidebarBlock;

const FilterBlockTitle = styled.div`
  color: ${colors.primary};
  font-size: 14px;
  margin-top: 10px;
  cursor: pointer;
`;
